import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { Input } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import { Button } from "@tonic/shared/components/ui";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@tonic/shared/components/ui";
import { Textarea } from "@tonic/shared/components/ui";
import { Switch } from "@tonic/shared/components/ui";
import { useUpdateProduct } from '../../../hooks/useProduct';
import { useProductForm } from '../../../hooks/useProductForm';
import { productUpdateSchema } from '../../../schemas/product';
import PropTypes from 'prop-types';

export function BasicInfoTab({ product }) {
  const updateMutation = useUpdateProduct(product.id);
  const {
    data: formData,
    errors,
    setFieldValue,
    validate
  } = useProductForm({
    name: product.name,
    sku: product.sku,
    description: product.description,
    category: product.category,
    status: product.status,
    tags: product.tags.join(', '),
    isVisibleInCommerce: product.isVisibleInCommerce
  }, productUpdateSchema.pick({
    name: true,
    sku: true,
    description: true,
    category: true,
    status: true,
    isVisibleInCommerce: true
  }).extend({
    tags: productUpdateSchema.shape.tags.transform(tags => tags.join(', '))
  }));

  const getFieldError = (field) => 
    errors.find(error => error.path[0] === field)?.message;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      });
      // TODO: Show success toast
    } catch (error) {
      console.error('Failed to update product:', error);
      // TODO: Show error toast
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFieldValue('name', e.target.value)}
                placeholder="Enter product name"
                aria-invalid={!!getFieldError('name')}
              />
              {getFieldError('name') && (
                <p className="text-sm text-red-500">{getFieldError('name')}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFieldValue('sku', e.target.value)}
                placeholder="Enter SKU"
                aria-invalid={!!getFieldError('sku')}
              />
              {getFieldError('sku') && (
                <p className="text-sm text-red-500">{getFieldError('sku')}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFieldValue('description', e.target.value)}
              placeholder="Enter product description"
              rows={4}
              aria-invalid={!!getFieldError('description')}
            />
            {getFieldError('description') && (
              <p className="text-sm text-red-500">{getFieldError('description')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFieldValue('category', value)}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electronics">Electronics</SelectItem>
                  <SelectItem value="apparel">Apparel</SelectItem>
                  <SelectItem value="accessories">Accessories</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFieldValue('status', value)}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="discontinued">Discontinued</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <div className="space-y-0.5">
              <Label>Visible in Commerce</Label>
              <p className="text-sm text-muted-foreground">
                Show this product in the personal commerce app
              </p>
            </div>
            <Switch
              checked={formData.isVisibleInCommerce}
              onCheckedChange={(checked) => setFieldValue('isVisibleInCommerce', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFieldValue('tags', e.target.value)}
              placeholder="Enter tags separated by commas"
              aria-invalid={!!getFieldError('tags')}
            />
            {getFieldError('tags') && (
              <p className="text-sm text-red-500">{getFieldError('tags')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end mt-4 space-x-2">
        <Button 
          type="button" 
          variant="outline"
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}

BasicInfoTab.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    sku: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['active', 'discontinued', 'draft']).isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    isVisibleInCommerce: PropTypes.bool.isRequired
  }).isRequired
}; 